import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  FolderOpen,
  FileDown,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  Archive,
  Copy,
  Check,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  createPublicFilesJob,
  getPublicFilesJob,
  listPublicFilesJobs,
  requestZipArchive,
  getZipStatus,
  getZipDownloadUrl,
  canUsePublicFiles,
  type PublicFilesJob,
} from '../lib/publicFiles';

const FCC_PREFIX = 'https://publicfiles.fcc.gov/';

interface PublicFilesPageProps {
  userEmail: string | null;
}

export const PublicFilesPage: React.FC<PublicFilesPageProps> = ({ userEmail }) => {
  if (!canUsePublicFiles(userEmail)) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold text-black mb-2">Not Authorized</h1>
        <p className="text-gray-600">This page is restricted to authorized users.</p>
      </div>
    );
  }

  return <PublicFilesContent />;
};

function PublicFilesContent() {
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [historyExpanded, setHistoryExpanded] = useState(true);

  const {
    data: activeJob,
    isLoading: activeJobLoading,
  } = useQuery({
    queryKey: ['public-files-job', activeJobId],
    queryFn: () => getPublicFilesJob(activeJobId!),
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'done' || status === 'failed') return false;
      return 5000;
    },
  });

  const {
    data: allJobs,
    isLoading: jobsLoading,
    refetch: refetchJobs,
  } = useQuery({
    queryKey: ['public-files-jobs'],
    queryFn: listPublicFilesJobs,
    refetchInterval: 15000,
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setUrlError(null);
      setSubmitError(null);

      const trimmed = urlInput.trim();
      if (!trimmed.startsWith(FCC_PREFIX)) {
        setUrlError(`URL must start with ${FCC_PREFIX}`);
        return;
      }

      try {
        setSubmitting(true);
        const { job_id } = await createPublicFilesJob(trimmed);
        setActiveJobId(job_id);
        setUrlInput('');
        refetchJobs();
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Failed to create job');
      } finally {
        setSubmitting(false);
      }
    },
    [urlInput, refetchJobs],
  );

  const isJobRunning =
    !!activeJob && activeJob.status !== 'done' && activeJob.status !== 'failed';

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-300/50">
          <Download className="w-8 h-8 text-blue-700" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-black">OPIF Downloader</h1>
          <p className="text-sm text-gray-500">
            Download station archives from the FCC's Online Public Inspection Files (
            <a
              href="https://publicfiles.fcc.gov/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              OPIF
            </a>
            )
          </p>
        </div>
      </div>

      {/* URL Input */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm mb-6">
        <h2 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-blue-600" />
          Start New Download
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="fcc-url" className="block text-sm font-medium text-gray-700 mb-1">
              OPIF URL (publicfiles.fcc.gov)
            </label>
            <input
              id="fcc-url"
              type="url"
              value={urlInput}
              onChange={(e) => { setUrlInput(e.target.value); setUrlError(null); }}
              placeholder="https://publicfiles.fcc.gov/tv-profile/WTVD/"
              className={`w-full rounded-lg border px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-colors ${
                urlError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
              }`}
            />
            {urlError && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />{urlError}
              </p>
            )}
          </div>
          {submitError && (
            <p className="text-sm text-red-600 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 shrink-0" />{submitError}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting || isJobRunning}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            {submitting ? 'Starting…' : 'Start Download'}
          </button>
          {isJobRunning && (
            <p className="text-xs text-gray-500">A download is already in progress.</p>
          )}
        </form>
      </div>

      {/* Active Job + ZIP progress (current session only) */}
      {activeJobId && (
        <ActiveJobCard job={activeJob ?? null} loading={activeJobLoading} />
      )}
      {activeJob?.status === 'done' && (
        <ZipSection jobId={activeJobId!} initialZipStatus={activeJob.zip_status} />
      )}

      {/* Job History */}
      <div className="bg-white border-2 border-gray-200 rounded-xl shadow-sm overflow-hidden mt-6">
        <button
          onClick={() => setHistoryExpanded(!historyExpanded)}
          className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-bold text-black">Job History</h2>
            {allJobs && <span className="text-sm text-gray-500">({allJobs.length})</span>}
          </div>
          {historyExpanded
            ? <ChevronUp className="w-5 h-5 text-gray-400" />
            : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>

        {historyExpanded && (
          <div className="border-t border-gray-100">
            {jobsLoading ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : !allJobs || allJobs.length === 0 ? (
              <p className="px-5 py-5 text-sm text-gray-400 italic">No jobs yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="px-5 py-3">URL</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Progress</th>
                      <th className="px-5 py-3 whitespace-nowrap">Started</th>
                      <th className="px-5 py-3">Download</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {allJobs.map((job) => (
                      <tr
                        key={job.id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => {
                          if (job.status !== 'done' && job.status !== 'failed') {
                            setActiveJobId(job.id);
                          }
                        }}
                      >
                        <td className="px-5 py-3 max-w-xs">
                          <CopyableUrl url={job.start_url} />
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <StatusBadge status={job.status} />
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-gray-700">
                          {job.downloaded}/{job.total_files > 0 ? job.total_files : '?'}
                          {job.failed_count > 0 && (
                            <span className="ml-2 text-xs text-red-500">({job.failed_count} failed)</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-gray-500 whitespace-nowrap">
                          {new Date(job.started_at).toLocaleString()}
                        </td>
                        <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                          {job.status === 'done' && <ZipDownloadButton job={job} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Copyable URL cell                                                  */
/* ------------------------------------------------------------------ */

function CopyableUrl({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-1.5 group">
      <span className="text-gray-700 text-sm truncate" title={url}>{url}</span>
      <button
        onClick={handleCopy}
        className="shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-700"
        title="Copy URL"
      >
        {copied
          ? <Check className="w-3.5 h-3.5 text-green-500" />
          : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ZIP Download Button (inline in history table)                     */
/* ------------------------------------------------------------------ */

function ZipDownloadButton({ job }: { job: PublicFilesJob }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isReady = job.zip_status === 'ready';
  const isBuilding = !job.zip_status || job.zip_status === 'queued' || job.zip_status === 'zipping';
  const isFailed = job.zip_status === 'failed';

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = await getZipDownloadUrl(job.id);
      window.location.href = url;
    } catch {
      setError('Failed to get download link');
    } finally {
      setLoading(false);
    }
  };

  if (isBuilding) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-gray-400">
        <RefreshCw className="w-3 h-3 animate-spin" /> Preparing…
      </span>
    );
  }

  if (isFailed) {
    return <span className="text-xs text-red-500">{job.zip_error || 'Failed'}</span>;
  }

  if (isReady) {
    return (
      <div className="flex flex-col gap-1">
        <button
          onClick={handleDownload}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs font-semibold text-green-700 hover:text-green-900 disabled:opacity-50 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          {loading ? 'Opening…' : 'Download'}
        </button>
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
    );
  }

  return null;
}

/* ------------------------------------------------------------------ */
/*  ZIP Section (shown below active job card while archive builds)    */
/* ------------------------------------------------------------------ */

interface ZipSectionProps {
  jobId: string;
  initialZipStatus: PublicFilesJob['zip_status'];
}

function ZipSection({ jobId, initialZipStatus }: ZipSectionProps) {
  const [zipStatus, setZipStatus] = useState<string | null>(initialZipStatus ?? null);
  const [zipSizeBytes, setZipSizeBytes] = useState<number | null>(null);
  const [zipError, setZipError] = useState<string | null | undefined>(null);
  const [retrying, setRetrying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-trigger ZIP if status is null (covers existing done jobs and edge cases)
  useEffect(() => {
    if (!zipStatus) {
      requestZipArchive(jobId)
        .then((r) => setZipStatus(r.zip_status))
        .catch(() => setZipStatus('failed')); // surface failure (with retry) rather than a permanent "queued"
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Sync if parent switches to a different job
  useEffect(() => {
    setZipStatus(initialZipStatus ?? null);
    setZipSizeBytes(null);
    setZipError(null);
  }, [jobId, initialZipStatus]);

  // Polling while in progress
  useEffect(() => {
    const shouldPoll = zipStatus === 'queued' || zipStatus === 'zipping';

    if (!shouldPoll) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    const doPoll = async () => {
      try {
        const result = await getZipStatus(jobId);
        setZipStatus(result.zip_status);
        setZipSizeBytes(result.zip_size_bytes ?? null);
        setZipError(result.zip_error);
      } catch {
        // silently retry on transient errors
      }
    };

    doPoll();
    pollRef.current = setInterval(doPoll, 5000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobId, zipStatus]);

  const handleRetry = async () => {
    try {
      setRetrying(true);
      setZipError(null);
      const result = await requestZipArchive(jobId);
      setZipStatus(result.zip_status);
    } catch (err) {
      setZipError(err instanceof Error ? err.message : 'Failed to restart download preparation');
    } finally {
      setRetrying(false);
    }
  };

  const formatSize = (bytes: number | null): string => {
    if (bytes === null) return '';
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const isInProgress = !zipStatus || zipStatus === 'queued' || zipStatus === 'zipping';
  const isReady = zipStatus === 'ready';
  const isFailed = zipStatus === 'failed';

  return (
    <div
      className={`bg-white border-2 rounded-xl p-6 shadow-sm mb-6 ${
        isFailed
          ? 'border-red-300'
          : isReady
          ? 'border-green-300'
          : 'border-blue-300'
      }`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Archive className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-bold text-black">Download Files</h2>
      </div>

      {/* In-progress state (also covers null — auto-trigger fires in background) */}
      {isInProgress && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-500 shrink-0" />
            <span className="text-sm font-medium text-gray-800">Preparing your download…</span>
          </div>
          <p className="text-xs text-gray-500 ml-8">
            Bundling all files into a single archive. This may take a few minutes for large collections.
          </p>
        </div>
      )}

      {/* Ready state */}
      {isReady && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
            <span className="text-sm font-semibold text-green-700">Ready to download</span>
            {zipSizeBytes !== null && (
              <span className="text-sm text-gray-500">— {formatSize(zipSizeBytes)}</span>
            )}
          </div>
          <button
            onClick={async () => {
              setDownloading(true);
              try {
                const url = await getZipDownloadUrl(jobId);
                window.location.href = url;
              } finally {
                setDownloading(false);
              }
            }}
            disabled={downloading}
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-medium text-sm hover:bg-green-700 disabled:opacity-60 transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'Starting download…' : 'Download All Files'}
          </button>
          <p className="text-xs text-gray-400">The server streams the ZIP directly to your browser — no size limits.</p>
        </div>
      )}

      {/* Failed state */}
      {isFailed && (
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700">Download preparation failed</p>
              {zipError && (
                <p className="text-xs text-red-600 mt-1 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {zipError}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? 'Retrying…' : 'Try Again'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Active Job Card                                                    */
/* ------------------------------------------------------------------ */

interface ActiveJobCardProps {
  job: PublicFilesJob | null;
  loading: boolean;
}

function ActiveJobCard({ job, loading }: ActiveJobCardProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!job || job.status === 'done' || job.status === 'failed') return;
    const started = new Date(job.started_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - started) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [job?.started_at, job?.status]);

  if (loading && !job) {
    return (
      <div className="bg-white border-2 border-blue-200 rounded-xl p-6 shadow-sm mb-6 flex items-center gap-3">
        <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
        <span className="text-sm text-gray-600">Loading job status…</span>
      </div>
    );
  }

  if (!job) return null;

  const pct = job.total_files > 0 ? Math.round((job.downloaded / job.total_files) * 100) : 0;

  // Rate (files/min) and ETA — only meaningful after 15s with some progress
  const rate = elapsed > 15 && job.downloaded > 0 ? (job.downloaded / elapsed) * 60 : null;
  const remaining = job.total_files > 0 ? job.total_files - job.downloaded : null;
  const etaSecs = rate && remaining ? Math.ceil(remaining / (rate / 60)) : null;

  const fmtElapsed = (s: number) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60); const sec = s % 60;
    return `${m}m ${sec.toString().padStart(2, '0')}s`;
  };
  const fmtEta = (s: number) => {
    if (s < 60) return `~${s}s left`;
    return `~${Math.ceil(s / 60)}m left`;
  };

  const statusLabel: Record<PublicFilesJob['status'], string> = {
    queued: 'Queued — waiting for local agent…',
    crawling: 'Discovering files…',
    downloading: 'Downloading files',
    done: 'Done',
    failed: 'Failed',
  };

  const isDone = job.status === 'done';
  const isFailed = job.status === 'failed';
  const isActive = !isDone && !isFailed;
  const isDownloading = job.status === 'downloading';
  const isCrawling = job.status === 'crawling' || job.status === 'queued';

  return (
    <div
      className={`bg-white border-2 rounded-xl p-6 shadow-sm mb-6 ${
        isFailed ? 'border-red-300' : isDone ? 'border-green-300' : 'border-blue-300'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {isActive && <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />}
          {isDone && <CheckCircle className="w-5 h-5 text-green-600" />}
          {isFailed && <AlertTriangle className="w-5 h-5 text-red-500" />}
          <h2 className="text-lg font-bold text-black">Active Job</h2>
        </div>
        <div className="flex items-center gap-3">
          {isActive && elapsed > 0 && (
            <span className="text-xs text-gray-400 tabular-nums">{fmtElapsed(elapsed)}</span>
          )}
          <StatusBadge status={job.status} />
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-1 truncate" title={job.start_url}>
        <span className="font-medium">URL:</span> {job.start_url}
      </p>

      <p className="text-sm text-gray-500 mb-4">{statusLabel[job.status]}</p>

      {/* Progress bar */}
      <div className="mb-2">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>
            {job.downloaded} / {job.total_files > 0 ? job.total_files : '—'} files
            {rate && <span className="ml-2 text-gray-400">{Math.round(rate)}/min</span>}
          </span>
          <span className="tabular-nums">
            {etaSecs ? fmtEta(etaSecs) : job.total_files > 0 ? `${pct}%` : ''}
          </span>
        </div>

        {/* Determinate bar (downloading with known total) */}
        {isDownloading && job.total_files > 0 && (
          <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-[3000ms] ease-out relative overflow-hidden"
              style={{ width: `${pct}%` }}
            >
              {/* shimmer sweep */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-[shimmer_2s_ease-in-out_infinite]" />
            </div>
          </div>
        )}

        {/* Indeterminate bar (crawling / queued / downloading with unknown total) */}
        {(isCrawling || (isDownloading && job.total_files === 0)) && (
          <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div className="h-full w-2/5 rounded-full bg-blue-400 animate-[indeterminate_1.4s_ease-in-out_infinite]" />
          </div>
        )}

        {/* Done / failed */}
        {(isDone || isFailed) && job.total_files > 0 && (
          <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${isFailed ? 'bg-red-500' : 'bg-green-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>

      {job.failed_count > 0 && (
        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5" />
          {job.failed_count} file{job.failed_count !== 1 ? 's' : ''} failed to download
        </p>
      )}

      {job.error && (
        <p className="text-xs text-red-600 mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {job.error}
        </p>
      )}

    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Status Badge                                                       */
/* ------------------------------------------------------------------ */

function StatusBadge({ status }: { status: PublicFilesJob['status'] }) {
  const config: Record<
    PublicFilesJob['status'],
    { label: string; cls: string }
  > = {
    queued: { label: 'Queued', cls: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20' },
    crawling: { label: 'Crawling', cls: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
    downloading: { label: 'Downloading', cls: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20' },
    done: { label: 'Done', cls: 'bg-green-50 text-green-700 ring-green-600/20' },
    failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 ring-red-600/20' },
  };

  const { label, cls } = config[status] ?? { label: status, cls: 'bg-gray-100 text-gray-700 ring-gray-600/20' };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  );
}
